// Details panel for a hosted site: editable description, AI summary (generate / regenerate), version history

import { useId, useState, type FormEvent } from 'react'
import { Loader2, Pencil, RefreshCw, Sparkles } from 'lucide-react'
import type { HostedSite } from '../../hooks/use-hosted-sites'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import { buttonPrimaryClass, buttonSecondaryClass, inputClass } from '../ui/styles'
import { SiteVersions } from './site-versions'
import { DESCRIPTION_MAX } from './utils'

const SUMMARY_CLAMP_CHARS = 180

export interface SiteDetailsProps {
  /** Resolves when saved; rejects with the error so the editor stays open */
  onSaveDescription: (site: HostedSite, description: string | null) => Promise<void>
  summaryPending: boolean
  summaryError: string | null
  onRegenerate: (id: string) => void
}

export function SiteDisclosure({ site, onSaveDescription, summaryPending, summaryError, onRegenerate }: SiteDetailsProps & { site: HostedSite }) {
  const t = useT()
  const id = useId()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [summaryFull, setSummaryFull] = useState(false)

  const startEdit = () => {
    setDraft(site.description ?? '')
    setEditing(true)
  }

  const handleSave = async (e: FormEvent) => {
    e.preventDefault()
    const trimmed = draft.trim().slice(0, DESCRIPTION_MAX)
    const next = trimmed.length === 0 ? null : trimmed
    if (next === (site.description ?? null)) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSaveDescription(site, next)
      setEditing(false)
    } catch {
      // The caller reports the error; keep the draft so it can be fixed and saved again
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-3 space-y-4 border-t border-border pt-3">
      <div className="space-y-1.5">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">{t('sites.description.label')}</h4>
        {editing ? (
          <form onSubmit={handleSave} className="space-y-2">
            <label htmlFor={`${id}-description`} className="sr-only">
              {t('sites.description.label')}
            </label>
            <input
              id={`${id}-description`}
              type="text"
              autoFocus
              value={draft}
              maxLength={DESCRIPTION_MAX}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditing(false)
                }
              }}
              className={inputClass}
            />
            <div className="flex gap-2">
              <button type="submit" disabled={saving} className={cn(buttonPrimaryClass, 'min-h-8 py-1')}>
                {saving ? t('settings.saving') : t('settings.save')}
              </button>
              <button type="button" onClick={() => setEditing(false)} className={cn(buttonSecondaryClass, 'min-h-8 py-1')}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        ) : (
          <div className="flex items-start gap-2">
            <p className={cn('min-w-0 flex-1 whitespace-pre-line break-words text-sm', site.description ? 'text-fg-muted' : 'text-fg-subtle')}>
              {site.description || t('sites.description.none')}
            </p>
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs text-accent-text hover:bg-surface-hover"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              {site.description ? t('sites.description.edit') : t('sites.description.add')}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-fg-subtle">
          <Sparkles className="h-3.5 w-3.5 text-accent-text" aria-hidden="true" />
          {t('sites.summary.label')}
        </h4>
        {site.summary ? (
          <>
            <p className={cn('text-sm leading-relaxed text-fg-muted', !summaryFull && 'line-clamp-3')}>{site.summary}</p>
            <div className="flex flex-wrap items-center gap-2">
              {site.summary.length > SUMMARY_CLAMP_CHARS && (
                <button
                  type="button"
                  onClick={() => setSummaryFull((v) => !v)}
                  aria-expanded={summaryFull}
                  className="cursor-pointer text-xs text-accent-text underline-offset-2 hover:underline"
                >
                  {summaryFull ? t('sites.summary.showLess') : t('sites.summary.showMore')}
                </button>
              )}
              <button
                type="button"
                onClick={() => onRegenerate(site.id)}
                disabled={summaryPending}
                className="inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-surface-hover hover:text-fg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {summaryPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
                {summaryPending ? t('sites.summary.regenerating') : t('sites.summary.regenerate')}
              </button>
            </div>
          </>
        ) : (
          <button type="button" onClick={() => onRegenerate(site.id)} disabled={summaryPending} className={cn(buttonSecondaryClass, 'min-h-8 py-1 text-xs')}>
            {summaryPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
            {summaryPending ? t('sites.summary.generating') : t('sites.summary.generate')}
          </button>
        )}
        {summaryError && (
          <p role="alert" className="text-xs text-danger">
            {summaryError}
          </p>
        )}
      </div>

      <SiteVersions site={site} />
    </div>
  )
}
